/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { transparentize } from 'polished';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { getNodesByFilterId } from '@/features/canvas/api/filter-nodes';
import { getFiltersByRoadmapId } from '@/features/canvas/api/filters';
import {
  createMappingTemplate,
  CsvUploadResponse,
  deleteMappingTemplate,
  listMappingTemplates,
  updateMappingTemplate,
} from '@/features/canvas/api/import';
import { getAllBudgetsForRoadmap } from '@/features/canvas/api/initiative_budgets';
import { csvImportNewNode, getAllRoadmapNodes } from '@/features/canvas/api/node';
import { LinkingDialog } from '@/features/canvas/components/ImportAccordionContent/LinkingDialog';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useImportInitiativeContext } from '@/features/canvas/contexts/import-initiative-context';
import { InitiativeToBeImported } from '@/features/canvas/types/initiative';
import {
  LinkageColumn,
  LinkageRow,
  LinkedColumn,
  LinkedColumnType,
} from '@/features/canvas/types/linkage';
import { applyTemplate, prepareMappingsForTemplate } from '@/features/canvas/utils/applyTemplate';
import { autoDetectMappings, detectIdColumn } from '@/features/canvas/utils/csvColumnMatcher';
import { parseBudget } from '@/features/canvas/utils/format-budget';
import { getTransformedLinkedValue } from '@/features/canvas/utils/get-transformed-linked-value';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

import { ImportList } from '../List';

interface CsvLinkageProps {
  onClearServerData?: () => void;
  serverCsvData?: CsvUploadResponse | null;
}

const CsvLinkage = ({ serverCsvData, onClearServerData }: CsvLinkageProps) => {
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const queryClient = useQueryClient();
  const {
    roadmap,
    roadmapVisible,
    dropdownVisible,
    budgetDropdownVisible,
    filters,
    sortedVerticalSections,
    sortedTimePeriods,
  } = useEditorContext();
  const {
    isInitiativeImportRunning,
    budgetFieldsToBeCreatedFromImport,
    setBudgetFieldsToBeCreatedFromImport,
  } = useImportInitiativeContext();

  const [isLinkingDialogOpen, setIsLinkingDialogOpen] = useState(false);
  const [linkingColumns, setLinkingColumns] = useState<LinkageColumn[]>([]);
  const [linkingRows, setLinkingRows] = useState<LinkageRow[]>([]);
  const [linkedColumns, setLinkedColumns] = useState<LinkedColumn[]>([]);
  const [filename, setFilename] = useState<string>('');
  const [idColumn, setIdColumn] = useState<LinkageColumn | null>(null);
  const [editedCsvValues, setEditedCsvValuesRaw] = useState<Record<string, Record<string, string>>>(
    {}
  );

  const setEditedCsvValues = setEditedCsvValuesRaw;

  // Ref to track processed CSV data to prevent unnecessary re-processing
  const processedCsvDataRef = useRef<typeof serverCsvData | null>(null);

  const [initiativesToPlaceOnRoadmap, setInitiativesToPlaceOnRoadmap] = useState<
    InitiativeToBeImported[]
  >([]);

  // Mutation for direct import of initiatives with existing IDs
  const { mutateAsync: directImportMutation } = useMutation(
    ({ csvNode }: { csvNode: components['schemas']['NodeImportCSV'] }) =>
      roadmap?.id
        ? csvImportNewNode({ roadmapId: roadmap.id, csvNode })
        : Promise.reject(new Error('No roadmap')),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(['nodes']);
        void queryClient.invalidateQueries(['initiatives']);
        // Also invalidate roadmap nodes to refetch dependencies
        if (roadmap?.id) {
          void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
          // Invalidate budget data to refresh canvas display
          void queryClient.invalidateQueries([getAllBudgetsForRoadmap.name, roadmap.id]);
          // Invalidate filter queries to refresh filter display
          void queryClient.invalidateQueries([getFiltersByRoadmapId.name, roadmap.id]);
          // Invalidate filter nodes for all filters
          if (filters) {
            filters.forEach(filter => {
              if (filter.id) {
                void queryClient.invalidateQueries([getNodesByFilterId.name, filter.id]);
              }
            });
          }
        }
      },
    }
  );

  // Templates functionality
  const { data: templates = [] } = useQuery(
    ['mapping-templates', roadmap?.id],
    () => (roadmap?.id ? listMappingTemplates(roadmap.id) : []),
    {
      enabled: !!roadmap?.id,
    }
  );

  const createTemplateMutation = useMutation(
    ({ name, mappings }: { mappings: LinkedColumn[]; name: string }) =>
      roadmap?.id
        ? createMappingTemplate(roadmap.id, name, mappings)
        : Promise.reject(new Error('No roadmap')),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(['mapping-templates', roadmap?.id]);
        showToast(
          'success',
          t('editor.sidebar.data_tab.template_saved_success', 'Template saved successfully')
        );
      },
      onError: (error: any) => {
        console.error('Failed to save template:', error);
        void showToast(
          'error',
          t('editor.sidebar.data_tab.template_save_error', 'Failed to save template')
        );
      },
    }
  );

  const updateTemplateMutation = useMutation(
    ({
      templateId,
      name,
      mappings,
    }: {
      mappings: LinkedColumn[];
      name: string;
      templateId: number;
    }) =>
      roadmap?.id
        ? updateMappingTemplate(roadmap.id, templateId, name, mappings)
        : Promise.reject(new Error('No roadmap')),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(['mapping-templates', roadmap?.id]);
        showToast(
          'success',
          t('editor.sidebar.data_tab.template_updated_success', 'Template updated successfully')
        );
      },
      onError: (error: any) => {
        console.error('Failed to update template:', error);
        void showToast(
          'error',
          t('editor.sidebar.data_tab.template_update_error', 'Failed to update template')
        );
      },
    }
  );

  const deleteTemplateMutation = useMutation(
    (templateId: number) =>
      roadmap?.id
        ? deleteMappingTemplate(roadmap.id, templateId)
        : Promise.reject(new Error('No roadmap')),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(['mapping-templates', roadmap?.id]);
        showToast(
          'success',
          t('editor.sidebar.data_tab.template_deleted_success', 'Template deleted successfully')
        );
      },
      onError: (error: any) => {
        console.error('Failed to delete template:', error);
        void showToast(
          'error',
          t('editor.sidebar.data_tab.template_delete_error', 'Failed to delete template')
        );
      },
    }
  );

  // Template handler functions
  const handleApplyTemplate = useCallback(
    (templateId: number) => {
      const template = templates.find(tpl => tpl.id === templateId);
      if (template && linkingColumns.length > 0) {
        const appliedMappings = applyTemplate(template, linkingColumns);
        setLinkedColumns(appliedMappings);
        showToast(
          'success',
          t('editor.sidebar.data_tab.template_applied_success', 'Template applied successfully')
        );
      }
    },
    [templates, linkingColumns, showToast, t]
  );

  const handleSaveTemplate = useCallback(
    (name: string) => {
      if (linkedColumns.length > 0) {
        const mappingsToSave = prepareMappingsForTemplate(linkedColumns, linkingColumns);

        // Check if a template with this name already exists
        const existingTemplate = templates.find(template => template.name === name);

        if (existingTemplate) {
          // Update existing template
          updateTemplateMutation.mutate({
            templateId: existingTemplate.id,
            name,
            mappings: mappingsToSave,
          });
        } else {
          // Create new template
          createTemplateMutation.mutate({ name, mappings: mappingsToSave });
        }
      }
    },
    [linkedColumns, linkingColumns, templates, createTemplateMutation, updateTemplateMutation]
  );

  const handleDeleteTemplate = useCallback(
    (templateId: number) => {
      deleteTemplateMutation.mutate(templateId);
    },
    [deleteTemplateMutation]
  );

  // Convert server CSV data to LinkingDialog format
  const convertServerDataToLinkingFormat = useCallback((data: CsvUploadResponse) => {
    // Create columns with UUIDs (similar to Papa.parse transformation)
    const columns: LinkageColumn[] = data.headers.map(header => ({
      id: uuidv4(),
      name: header,
    }));

    // Create rows with cells for each column
    const rows: LinkageRow[] = data.preview.map(rowData => {
      const cells = columns.map(column => ({
        id: uuidv4(),
        value: rowData[column.name] || '',
      }));
      return {
        id: uuidv4(),
        cells,
      };
    });

    return { columns, rows };
  }, []);

  const onCSVLinkageDialogClose = useCallback(() => {
    setIsLinkingDialogOpen(false);
    setLinkingColumns([]);
    setLinkingRows([]);
    setLinkedColumns([]);
    setEditedCsvValues({}); // Reset edited values when dialog closes
    // Clear server data when dialog closes
    if (serverCsvData && onClearServerData) {
      onClearServerData();
    }
  }, [serverCsvData, onClearServerData, setEditedCsvValues]);

  // Handle server-side CSV data - only run when serverCsvData actually changes
  useEffect(() => {
    // Only process if this is new CSV data we haven't seen before
    if (serverCsvData && serverCsvData !== processedCsvDataRef.current) {
      processedCsvDataRef.current = serverCsvData;

      const { columns, rows } = convertServerDataToLinkingFormat(serverCsvData);

      // Detect ID column
      const detectedIdColumn = detectIdColumn(columns);
      setIdColumn(detectedIdColumn);

      // Filter out ID column from display columns
      const displayColumns = detectedIdColumn
        ? columns.filter(col => col.id !== detectedIdColumn.id)
        : columns;

      // Also filter out ID column cells from rows to match the filtered columns
      const displayRows = detectedIdColumn
        ? rows.map(row => {
            const idColumnIndex = columns.findIndex(col => col.id === detectedIdColumn.id);
            return {
              ...row,
              cells: row.cells.filter((_, index) => index !== idColumnIndex),
            };
          })
        : rows;

      setLinkingColumns(displayColumns);
      setLinkingRows(displayRows);
      setFilename(serverCsvData.filename);

      // Prepare fields for auto-detection
      const allFields = [
        ...roadmapVisible.map(f => ({
          id: `${f.id}-${f.type}-roadmap`,
          title: f.title,
          type: f.type,
        })),
        ...dropdownVisible.map(f => ({
          id: `${f.id}-${f.type}-dropdown`,
          title: f.title,
          type: f.type,
        })),
        ...budgetDropdownVisible.map(f => ({
          id: `${f.id}-${f.type}-budget-dropdown`,
          title: f.title,
          type: f.type,
        })),
      ];

      // Auto-detect mappings - filter out filters with undefined id
      const validFilters = filters.filter(filter => filter.id !== undefined) as {
        id: number;
        title: string;
      }[];
      // Use display columns for auto-mapping (excludes ID column)
      const autoMappings = autoDetectMappings(displayColumns, allFields, validFilters);
      if (autoMappings.length > 0) {
        setLinkedColumns(autoMappings);
        console.log(`Auto-detected ${autoMappings.length} column mappings`, autoMappings);
      }

      setIsLinkingDialogOpen(true);
    }
  }, [
    serverCsvData,
    convertServerDataToLinkingFormat,
    roadmapVisible,
    dropdownVisible,
    budgetDropdownVisible,
    filters,
  ]);

  // Helper to fix common character encoding issues in CSV
  const fixEncodingIssues = useCallback((value: string): string => {
    return value
      .replace(/Â£/g, '£') // Fix pound symbol
      .replace(/â‚¬/g, '€') // Fix euro symbol
      .replace(/Â¥/g, '¥') // Fix yen symbol
      .replace(/Â/g, '') // Remove stray Â characters
      .trim();
  }, []);

  // Helper to parse budget field value
  const parseBudgetField = useCallback(
    (value: string | undefined) => {
      if (!value?.trim()) return null;
      // Fix encoding issues before parsing
      const sanitizedValue = fixEncodingIssues(value);
      const parsed = parseBudget(sanitizedValue);
      if (parsed.value && !Number.isNaN(parsed.value)) {
        return { value: parsed.value, currency: parsed.currency };
      }
      return null;
    },
    [fixEncodingIssues]
  );

  // Helper function to extract budget values from linked columns
  const extractBudgetValues = useCallback(
    (
      row: LinkageRow,
      columns: LinkageColumn[],
      budgetColumns: {
        budgetCapEx?: LinkedColumn;
        budgetOpEx?: LinkedColumn;
        budgetSpent?: LinkedColumn;
        budgetTotal?: LinkedColumn;
      },
      editedValues?: Record<string, Record<string, string>>
    ) => {
      const { budgetTotal, budgetCapEx, budgetOpEx, budgetSpent } = budgetColumns;

      if (!budgetTotal && !budgetCapEx && !budgetOpEx && !budgetSpent) {
        return undefined;
      }

      const budgetValue: {
        capex?: number;
        currency?: string;
        opex?: number;
        spend?: number;
        total?: number;
      } = {};

      // Process each budget field
      const processField = (
        column: LinkedColumn | undefined,
        field: 'total' | 'capex' | 'opex' | 'spend'
      ) => {
        if (!column) return;
        const idx = columns.findIndex(col => col.id === column.importedColumnUuid);
        const originalValue = row.cells[idx]?.value || '';
        // Check for edited value
        const cellValue = editedValues?.[row.id]?.[column.importedColumnUuid] || originalValue;
        const parsed = parseBudgetField(cellValue);
        if (parsed) {
          budgetValue[field] = parsed.value;
          budgetValue.currency = budgetValue.currency || parsed.currency;
        }
      };

      processField(budgetTotal, 'total');
      processField(budgetCapEx, 'capex');
      processField(budgetOpEx, 'opex');
      processField(budgetSpent, 'spend');

      // Only keep budgetValue if at least one field has a value
      if (!budgetValue.total && !budgetValue.capex && !budgetValue.opex && !budgetValue.spend) {
        return undefined;
      }

      return budgetValue;
    },
    [parseBudgetField]
  );

  const handleSubmitLinking = useCallback(
    (editedValues: Record<string, Record<string, string>>) => {
      const titleColumn = linkedColumns.find(
        col => col.linkedTargetType === LinkedColumnType.title
      );
      if (!titleColumn) {
        showToast('error', t('editor.sidebar.data_tab.import_initiative_title_missing_error'));
        return;
      }

      const indexOfTitleColumn = linkingColumns.findIndex(
        col => col.id === titleColumn?.importedColumnUuid
      );

      if (indexOfTitleColumn < 0) {
        showToast('error', t('editor.sidebar.data_tab.import_initiative_title_missing_error'));
      }

      const filterLinkedColumns = linkedColumns.filter(
        lc => lc.linkedTargetType === LinkedColumnType.filter
      );
      const fieldLinkedColumns = linkedColumns.filter(
        lc =>
          ![LinkedColumnType.title, LinkedColumnType.filter].includes(lc.linkedTargetType as any)
      );

      // Helper function to get cell value, considering edited values
      const getCellValue = (row: LinkageRow, columnIndex: number, columnId?: string): string => {
        const originalValue = row.cells[columnIndex]?.value || '';
        // If we have a columnId and there are edited values for this row, use them
        if (columnId && editedValues[row.id] && editedValues[row.id][columnId]) {
          return editedValues[row.id][columnId];
        }
        return originalValue;
      };

      const initiativesToCreate = linkingRows.map((row, index) => {
        // Extract initiative ID if this is a re-import
        let existingInitiativeId: number | undefined;
        if (idColumn && serverCsvData) {
          // Get the ID from the original server data since it's filtered from linkingColumns
          const originalRow = serverCsvData.preview[index];

          if (originalRow && idColumn.name in originalRow) {
            const rawIdValue = originalRow[idColumn.name];
            const idValue = parseInt(rawIdValue);

            if (!Number.isNaN(idValue)) {
              existingInitiativeId = idValue;
            }
          }
        }
        const filterOptionValues = filterLinkedColumns
          .map(lc => {
            const indexOfLinkedFilterColumn = linkingColumns.findIndex(
              col => col.id === lc.importedColumnUuid
            );

            return {
              id: lc.linkedTargetId,
              value: getCellValue(row, indexOfLinkedFilterColumn, lc.importedColumnUuid),
            };
          })
          .filter(f => f.value);

        // Check for any budget-related columns
        const budgetTotalColumn = fieldLinkedColumns.find(
          lc => lc.linkedTargetType === LinkedColumnType.budgetTotal
        );
        const budgetCapExColumn = fieldLinkedColumns.find(
          lc => lc.linkedTargetType === LinkedColumnType.budgetCapEx
        );
        const budgetOpExColumn = fieldLinkedColumns.find(
          lc => lc.linkedTargetType === LinkedColumnType.budgetOpEx
        );
        const budgetSpentColumn = fieldLinkedColumns.find(
          lc => lc.linkedTargetType === LinkedColumnType.budgetSpent
        );

        // Debug logging for budget columns
        console.log('Budget columns found:', {
          budgetTotalColumn: budgetTotalColumn
            ? { type: budgetTotalColumn.linkedTargetType, id: budgetTotalColumn.linkedTargetId }
            : null,
          budgetCapExColumn: budgetCapExColumn
            ? { type: budgetCapExColumn.linkedTargetType, id: budgetCapExColumn.linkedTargetId }
            : null,
          budgetOpExColumn: budgetOpExColumn
            ? { type: budgetOpExColumn.linkedTargetType, id: budgetOpExColumn.linkedTargetId }
            : null,
          budgetSpentColumn: budgetSpentColumn
            ? { type: budgetSpentColumn.linkedTargetType, id: budgetSpentColumn.linkedTargetId }
            : null,
        });

        console.log(
          'All linkedColumns types:',
          linkedColumns.map(lc => lc.linkedTargetType)
        );
        console.log(
          'Field linked columns:',
          fieldLinkedColumns.map(lc => ({
            type: lc.linkedTargetType,
            title: lc.linkedTargetTitle,
            id: lc.linkedTargetId,
          }))
        );

        const fieldValues = fieldLinkedColumns
          .filter(lc => {
            // Exclude all budget-related field types
            if (
              lc.linkedTargetType === LinkedColumnType.budgetTotal ||
              lc.linkedTargetType === LinkedColumnType.budgetCapEx ||
              lc.linkedTargetType === LinkedColumnType.budgetOpEx ||
              lc.linkedTargetType === LinkedColumnType.budgetSpent
            ) {
              return false;
            }

            // Exclude dependencies - they're handled separately
            if (lc.linkedTargetType === LinkedColumnType.dependencies) {
              return false;
            }
            // Exclude children - they're handled separately
            if (lc.linkedTargetType === LinkedColumnType.children) {
              return false;
            }

            // Also exclude fields that are marked as budget custom fields
            const isBudgetField = budgetFieldsToBeCreatedFromImport.some(
              bf => bf.id === lc.linkedTargetId
            );
            return !isBudgetField;
          })
          .map(lc => {
            const indexOfLinkedFieldColumn = linkingColumns.findIndex(
              col => col.id === lc.importedColumnUuid
            );
            const value = getTransformedLinkedValue(
              getCellValue(row, indexOfLinkedFieldColumn, lc.importedColumnUuid),
              lc.linkedTargetType
            );

            return {
              id: lc.linkedTargetId,
              title: lc.linkedTargetTitle,
              value,
              type: lc.linkedTargetType,
              isBudgetField: false, // Regular fields are never budget fields
            };
          });

        // Process budget custom fields separately
        const budgetCustomFields = fieldLinkedColumns
          .filter(lc => {
            // Only include fields that are marked as budget custom fields
            const isBudgetCustomField = budgetFieldsToBeCreatedFromImport.some(
              bf => bf.id === lc.linkedTargetId
            );
            return isBudgetCustomField;
          })
          .map(lc => {
            const indexOfLinkedFieldColumn = linkingColumns.findIndex(
              col => col.id === lc.importedColumnUuid
            );
            const value = getTransformedLinkedValue(
              getCellValue(row, indexOfLinkedFieldColumn, lc.importedColumnUuid),
              lc.linkedTargetType
            );

            return {
              id: lc.linkedTargetId,
              title: lc.linkedTargetTitle,
              value,
              type: lc.linkedTargetType,
              isBudgetField: true, // Budget custom fields
            };
          });

        // Extract budget values using helper function
        const budgetValue = extractBudgetValues(
          row,
          linkingColumns,
          {
            budgetTotal: budgetTotalColumn,
            budgetCapEx: budgetCapExColumn,
            budgetOpEx: budgetOpExColumn,
            budgetSpent: budgetSpentColumn,
          },
          editedValues
        );

        console.log('Extracted budget value:', budgetValue);

        // Extract dependencies separately
        const dependenciesColumn = fieldLinkedColumns.find(
          lc => lc.linkedTargetType === LinkedColumnType.dependencies
        );
        let dependencies: string | null = null;
        if (dependenciesColumn) {
          const indexOfDependenciesColumn = linkingColumns.findIndex(
            col => col.id === dependenciesColumn.importedColumnUuid
          );
          const dependenciesValue = getCellValue(
            row,
            indexOfDependenciesColumn,
            dependenciesColumn.importedColumnUuid
          );
          // Only set if not empty
          if (dependenciesValue?.trim()) {
            dependencies = dependenciesValue.trim();
          }
        }

        console.log('Extracted dependencies:', dependencies);

        // Extract children separately
        const childrenColumn = fieldLinkedColumns.find(
          lc => lc.linkedTargetType === LinkedColumnType.children
        );
        let children: string | null = null;
        if (childrenColumn) {
          const indexOfChildrenColumn = linkingColumns.findIndex(
            col => col.id === childrenColumn.importedColumnUuid
          );
          const childrenValue = getCellValue(
            row,
            indexOfChildrenColumn,
            childrenColumn.importedColumnUuid
          );
          // Only set if not empty
          if (childrenValue?.trim()) {
            children = childrenValue.trim();
          }
        }

        console.log('Extracted children:', children);

        const initiative: InitiativeToBeImported = {
          id: index,
          title: getCellValue(row, indexOfTitleColumn, titleColumn.importedColumnUuid),
          isImportedFromCSV: true,
          fieldValues: [...fieldValues, ...budgetCustomFields], // Combine both types
          filterOptionValues,
          dependencies, // Add dependencies as separate property
          children, // Add children as separate property
          budgetValue,
          existingInitiativeId, // Add the existing ID for updates
        };

        return initiative;
      });

      // Split initiatives into those to update and those to create
      const initiativesToUpdate = initiativesToCreate.filter(i => i.existingInitiativeId);
      const newInitiatives = initiativesToCreate.filter(i => !i.existingInitiativeId);

      // Process updates immediately
      const processUpdates = async () => {
        const updatePromises = initiativesToUpdate.map(initiative => {
          // Get default section and time period IDs (will be ignored for updates anyway)
          const defaultSectionId = sortedVerticalSections?.[0]?.id || 1;
          const defaultTimePeriodId = sortedTimePeriods?.[0]?.id || 1;

          // Build the CSV node payload - need all required fields
          const csvNode: any = {
            title: initiative.title, // Required at top level
            x: 100, // Default position
            y: 100, // Default position
            sectionId: defaultSectionId, // Default section - will be ignored for updates
            timePeriodId: defaultTimePeriodId, // Default time period - will be ignored for updates
            isVisible: true, // Default visibility
            initiatives: {
              title: initiative.title,
              existing_id: initiative.existingInitiativeId,
              filters: initiative.filterOptionValues, // Add filter data
            },
          };

          // Add fields if present
          const allFields =
            initiative.fieldValues?.map(fv => ({
              title: fv.title,
              type: fv.type,
              value: fv.value,
              isBudgetField: fv.isBudgetField || false,
            })) || [];

          if (allFields.length > 0) {
            csvNode.initiatives.fields = allFields;
          }

          // Add budget if present (including sub-fields)
          if (initiative.budgetValue) {
            const budget: {
              currency: string;
              capex?: number;
              opex?: number;
              spend?: number;
              total?: number;
            } = {
              currency: initiative.budgetValue.currency || 'GBP',
            };

            if (initiative.budgetValue.total !== undefined) {
              budget.total = Number(initiative.budgetValue.total);
            }
            if (initiative.budgetValue.capex !== undefined) {
              budget.capex = Number(initiative.budgetValue.capex);
            }
            if (initiative.budgetValue.opex !== undefined) {
              budget.opex = Number(initiative.budgetValue.opex);
            }
            if (initiative.budgetValue.spend !== undefined) {
              budget.spend = Number(initiative.budgetValue.spend);
            }

            csvNode.initiatives.budget = budget;
          }

          // Add dependencies if present
          if (initiative.dependencies) {
            csvNode.initiatives.dependencies = initiative.dependencies;
          }

          // Add children if present
          if (initiative.children) {
            csvNode.initiatives.children = initiative.children;
          }

          console.log('Sending update payload:', JSON.stringify(csvNode, null, 2));
          return directImportMutation({ csvNode });
        });

        try {
          await Promise.all(updatePromises);

          // Invalidate roadmap nodes query to refetch with new dependencies
          if (roadmap?.id) {
            await queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
            // Also invalidate budget data to refresh canvas display
            await queryClient.invalidateQueries([getAllBudgetsForRoadmap.name, roadmap.id]);
            // Invalidate filter queries to refresh filter display
            await queryClient.invalidateQueries([getFiltersByRoadmapId.name, roadmap.id]);
            // Invalidate filter nodes for all filters
            if (filters) {
              filters.forEach(filter => {
                if (filter.id) {
                  void queryClient.invalidateQueries([getNodesByFilterId.name, filter.id]);
                }
              });
            }
          }

          // Show success message
          const message = t('editor.sidebar.data_tab.import_success', {
            defaultValue: `Updated {{updateCount}} initiative(s), {{createCount}} new initiative(s) ready to place`,
            updateCount: initiativesToUpdate.length,
            createCount: newInitiatives.length,
          });
          showToast('success', message);
        } catch (error) {
          console.error('Failed to update initiatives:', error);
          showToast(
            'error',
            t('editor.sidebar.data_tab.import_update_error', 'Failed to update some initiatives')
          );
        }
      };

      // Process updates if any
      if (initiativesToUpdate.length > 0) {
        void processUpdates();
      }

      // Only add new initiatives to the drag list
      setInitiativesToPlaceOnRoadmap(newInitiatives);
      if (!newInitiatives.length) {
        setFilename('');
      }
      onCSVLinkageDialogClose();
    },
    [
      linkedColumns,
      linkingColumns,
      linkingRows,
      showToast,
      t,
      budgetFieldsToBeCreatedFromImport,
      onCSVLinkageDialogClose,
      idColumn,
      serverCsvData,
      directImportMutation,
      sortedVerticalSections,
      sortedTimePeriods,
      extractBudgetValues,
      queryClient,
      roadmap,
      filters,
    ]
  );

  const handleUpdateList = (id: number, e?: React.DragEvent<HTMLDivElement>) => {
    if (e?.dataTransfer?.dropEffect !== 'none') {
      const newInitiatives = initiativesToPlaceOnRoadmap.filter(
        (i: InitiativeToBeImported) => i.id !== id
      );
      setInitiativesToPlaceOnRoadmap(newInitiatives);

      if (newInitiatives.length === 0) {
        setFilename('');
        // Clear budget fields context when all CSV initiatives are imported
        setBudgetFieldsToBeCreatedFromImport([]);
        // Clear server data when all initiatives are imported
        if (serverCsvData && onClearServerData) {
          onClearServerData();
        }
      }
    }
  };

  return (
    <ContentWrapper>
      {filename && <Filename>{filename}</Filename>}
      {Boolean(initiativesToPlaceOnRoadmap.length) && (
        <ImportList
          title={t('editor.sidebar.data_tab.checklist_title')}
          onUpdateList={handleUpdateList}
          list={initiativesToPlaceOnRoadmap}
          disabled={isInitiativeImportRunning}
        />
      )}
      <LinkingDialog
        open={isLinkingDialogOpen}
        onClose={onCSVLinkageDialogClose}
        columns={linkingColumns}
        rows={linkingRows}
        linkedColumns={linkedColumns}
        setLinkedColumns={setLinkedColumns}
        onSubmit={handleSubmitLinking}
        templates={templates}
        onApplyTemplate={handleApplyTemplate}
        onSaveTemplate={handleSaveTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        editedValues={editedCsvValues}
        setEditedValues={setEditedCsvValues}
      />
    </ContentWrapper>
  );
};

export { CsvLinkage };

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
`;

const Filename = styled.div`
  font-size: 1rem;
  width: 100%;
  border-radius: 1rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  color: ${({ theme }) => transparentize(0.3, theme.palette.brand.textPrimary)};
`;
